/**
 * Headless helpers for price precedent: display formatting and the delta a
 * table row shows against the quote on screen. The lookup rule itself —
 * nearest, last quoted, coverage — is the server's; nothing here re-derives it.
 */
import type {
  PrecedentCandidate,
  PrecedentRow,
  PriceLevels,
  RepeatPriceThresholds,
} from "../data/price-precedent-types";

/** Signed % of `quotedUnitPence` against the row's unit price. */
export function precedentDeltaPct(
  quotedUnitPence: number,
  row: PrecedentRow | null | undefined,
): number | null {
  if (!row || row.unit_price_pence <= 0) return null;
  return (
    ((quotedUnitPence - row.unit_price_pence) / row.unit_price_pence) * 100
  );
}

export type RepeatPriceVerdict = "ok" | "approval_required" | "blocked";

/** The guardrail's own decision for one move, mirroring resolve_price's
 *  assembly: hard band first, then the soft band. Directional. */
export function repeatPriceVerdict(
  movePct: number,
  t: RepeatPriceThresholds,
): RepeatPriceVerdict {
  if (movePct > t.up_hard || movePct < -t.down_hard) return "blocked";
  if (movePct > t.up_approval || movePct < -t.down_approval)
    return "approval_required";
  return "ok";
}

/** "+5.9%" / "−13.0%" / "0.0%". Uses a true minus sign. */
export function formatSignedPct(pct: number, dp = 1): string {
  const rounded = Number(pct.toFixed(dp));
  if (rounded === 0) return dp > 0 ? `0.${"0".repeat(dp)}%` : "0%";
  return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded).toFixed(dp)}%`;
}

/** "18.7p" below a pound, "£1.25" at or above. */
export function formatUnitPence(unitPence: number): string {
  if (unitPence >= 100) return `£${(unitPence / 100).toFixed(2)}`;
  return `${unitPence.toFixed(1)}p`;
}

/** "25 Aug 2026" — the row date as a person reads it. */
export function formatPrecedentDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * A CSR linked a prior job as this quote's precedent (search and link). Until
 * the backend re-resolves the levels with the manual match, present the link
 * as the last-ordered level — scaled by unit price to the quoted quantity —
 * so the CSR sees the effect of the link immediately. Not suggested: the
 * floor has not been checked against it.
 */
export function withLinkedPrecedent(
  levels: PriceLevels,
  candidate: PrecedentCandidate,
  quantity: number,
): PriceLevels {
  const unit =
    candidate.quantity > 0 ? candidate.price_pence / candidate.quantity : 0;
  const pence =
    candidate.quantity === quantity
      ? candidate.price_pence
      : Math.round(unit * quantity);
  const source: PrecedentRow = {
    date: candidate.date,
    quantity: candidate.quantity,
    price_pence: candidate.price_pence,
    unit_price_pence: unit,
    contribution_margin_pct: null,
    outcome: candidate.kind === "job" ? "ordered" : "quoted",
    source: "tharstern",
    match: "manual",
    reference: candidate.reference,
    url: candidate.url,
  };
  const linked = {
    kind:
      candidate.kind === "job"
        ? ("last_ordered" as const)
        : ("last_quoted" as const),
    price_pence: pence,
    unit_price_pence: unit,
    source,
    contribution_margin_pct: null,
    verdict: "unchecked" as const,
    verdict_reason: null,
    suggestable: false,
    not_suggestable_reason:
      "Linked by hand — the margin floor is checked when the quote is re-priced",
  };
  return {
    ...levels,
    unrecognised: false,
    // A hand-linked job is an exact match, so a similar order steps aside, as
    // it does when the engine recognises the order itself.
    levels: [
      linked,
      ...levels.levels.filter(
        (l) =>
          l.kind !== linked.kind &&
          !(linked.kind === "last_ordered" && l.kind === "similar_ordered"),
      ),
    ],
  };
}
